import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useCriancas } from '../hooks/useCriancas';
import { formatarDataBR, hojeISO } from '../common/data';

/**
 * Terapias externas e acompanhamento multidisciplinar — as duas últimas
 * promessas do card do AEE.
 *
 * A creche não executa a terapia: ela precisa saber que existe, com quem, e o
 * que o terapeuta orienta para a rotina da sala — senão a professora repete em
 * sala o que a terapia tenta desfazer.
 *
 * O contato do profissional **só aparece quando a família autorizou**, e quem
 * decide isso é o servidor: sem autorização, telefone e e-mail nem chegam aqui.
 */

interface Contato {
  telefone: string | null;
  email: string | null;
  liberado: boolean;
  motivo: string | null;
}

interface Terapia {
  id: number;
  id_aluno: number;
  especialidade: string;
  especialidade_outra: string | null;
  profissional: string;
  registro_conselho: string | null;
  clinica: string | null;
  frequencia: string | null;
  data_inicio: string;
  data_fim: string | null;
  situacao: string;
  orientacoes_para_creche: string | null;
  autorizacao_contato: boolean | number;
  autorizacao_por: string | null;
  autorizacao_em: string | null;
  aluno?: { id: number; nome: string } | null;
  contato: Contato;
}

interface Acompanhamento {
  id: number;
  id_aluno: number;
  id_terapia: number | null;
  tipo: string;
  data: string;
  participantes: string | null;
  resumo: string;
  encaminhamentos: string | null;
  proxima_acao_em: string | null;
  aluno?: { id: number; nome: string } | null;
  autor?: { id: number; nome: string } | null;
  terapia?: { id: number; profissional: string } | null;
}

const ESPECIALIDADES = [
  { valor: 'fonoaudiologia', label: 'Fonoaudiologia' },
  { valor: 'terapia_ocupacional', label: 'Terapia ocupacional' },
  { valor: 'psicologia', label: 'Psicologia' },
  { valor: 'fisioterapia', label: 'Fisioterapia' },
  { valor: 'psicopedagogia', label: 'Psicopedagogia' },
  { valor: 'neurologia', label: 'Neurologia' },
  { valor: 'nutricao', label: 'Nutrição' },
  { valor: 'outra', label: 'Outra' },
];

const SITUACOES = [
  { valor: 'ativa', label: 'Ativa' },
  { valor: 'suspensa', label: 'Suspensa' },
  { valor: 'encerrada', label: 'Encerrada' },
];

const TIPOS = [
  { valor: 'reuniao', label: 'Reunião' },
  { valor: 'relatorio_recebido', label: 'Relatório recebido' },
  { valor: 'contato_profissional', label: 'Contato com profissional' },
  { valor: 'observacao_equipe', label: 'Observação da equipe' },
];

const rotulo = (l: { valor: string; label: string }[], v: string) =>
  l.find((i) => i.valor === v)?.label || v;

function msgErro(e: any, padrao: string) {
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return typeof m === 'string' && m.trim() ? m : padrao;
}

const TERAPIA_VAZIA = {
  id_aluno: 0, especialidade: '', especialidade_outra: '', profissional: '',
  registro_conselho: '', clinica: '', telefone: '', email: '', frequencia: '',
  data_inicio: hojeISO(), orientacoes_para_creche: '',
};

export default function TerapiasSection() {
  const { criancas } = useCriancas();
  const [terapias, setTerapias] = useState<Terapia[]>([]);
  const [acomps, setAcomps] = useState<Acompanhamento[]>([]);
  const [erro, setErro] = useState('');
  const [criando, setCriando] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [nova, setNova] = useState<any>(TERAPIA_VAZIA);
  const [novoAcomp, setNovoAcomp] = useState<any>({
    id_aluno: 0, tipo: 'reuniao', data: hojeISO(), participantes: '',
    resumo: '', encaminhamentos: '', proxima_acao_em: '', id_terapia: '',
  });

  const carregar = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        api.get<Terapia[]>('/aee/terapias'),
        api.get<Acompanhamento[]>('/aee/acompanhamentos'),
      ]);
      setTerapias(t.data);
      setAcomps(a.data);
      setErro('');
    } catch (e) {
      setErro(msgErro(e, 'Não foi possível carregar terapias e acompanhamentos.'));
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  async function acao(fn: () => Promise<any>, padrao: string) {
    try {
      await fn();
      setErro('');
      await carregar();
    } catch (e) {
      setErro(msgErro(e, padrao));
    }
  }

  const criarTerapia = () =>
    acao(async () => {
      await api.post('/aee/terapias', {
        ...nova,
        especialidade_outra: nova.especialidade_outra || null,
        registro_conselho: nova.registro_conselho || null,
        clinica: nova.clinica || null,
        telefone: nova.telefone || null,
        email: nova.email || null,
        frequencia: nova.frequencia || null,
        orientacoes_para_creche: nova.orientacoes_para_creche || null,
      });
      setNova(TERAPIA_VAZIA);
      setCriando(false);
    }, 'Não foi possível cadastrar a terapia.');

  /** Autorizar exige quem e quando — o backend recusa sem isso. */
  const autorizarContato = (t: Terapia) => {
    const quem = window.prompt(
      'Quem da família autorizou a creche a falar com este profissional?\n'
      + '(nome de quem detém o poder familiar)',
    );
    if (!quem?.trim()) return;
    const quando = window.prompt('Em que data foi autorizado? (AAAA-MM-DD)', hojeISO());
    if (!quando?.trim()) return;
    acao(
      () => api.put(`/aee/terapias/${t.id}`, {
        autorizacao_contato: true, autorizacao_por: quem.trim(), autorizacao_em: quando.trim(),
      }),
      'Não foi possível registrar a autorização.',
    );
  };

  const revogarContato = (t: Terapia) =>
    acao(() => api.put(`/aee/terapias/${t.id}`, { autorizacao_contato: false }),
      'Não foi possível revogar a autorização.');

  const mudarSituacao = (t: Terapia, situacao: string) =>
    acao(() => api.put(`/aee/terapias/${t.id}`, { situacao }), 'Não foi possível atualizar.');

  const registrarAcomp = () =>
    acao(async () => {
      await api.post('/aee/acompanhamentos', {
        ...novoAcomp,
        id_terapia: novoAcomp.id_terapia || null,
        participantes: novoAcomp.participantes || null,
        encaminhamentos: novoAcomp.encaminhamentos || null,
        proxima_acao_em: novoAcomp.proxima_acao_em || null,
      });
      setNovoAcomp({
        id_aluno: 0, tipo: 'reuniao', data: hojeISO(), participantes: '',
        resumo: '', encaminhamentos: '', proxima_acao_em: '', id_terapia: '',
      });
      setRegistrando(false);
    }, 'Não foi possível registrar o acompanhamento.');

  const semAutorizacao = terapias.filter(
    (t) => t.situacao === 'ativa' && !t.contato.liberado,
  ).length;

  return (
    <section style={{ marginTop: 32 }}>
      <h2 style={{ margin: 0 }}>Terapias externas e acompanhamento multidisciplinar</h2>
      <p style={{ color: '#666', marginTop: 4 }}>
        O que a criança faz fora da creche, o que o profissional orienta para a sala, e o
        registro do que a equipe combina junto com a família.
      </p>

      {erro && <div className="alerta-erro" style={{ margin: '12px 0' }}>{erro}</div>}

      {semAutorizacao > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', padding: 10, borderRadius: 6, margin: '12px 0' }}>
          <strong>{semAutorizacao}</strong> terapia{semAutorizacao > 1 ? 's ativas' : ' ativa'} sem
          autorização da família para contato com o profissional. O telefone e o e-mail ficam
          indisponíveis até que a autorização seja registrada.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, margin: '12px 0' }}>
        <button className="btn-primario" onClick={() => setCriando((v) => !v)}>
          {criando ? 'Cancelar' : '+ Nova terapia'}
        </button>
        <button onClick={() => setRegistrando((v) => !v)}>
          {registrando ? 'Cancelar' : '+ Registrar acompanhamento'}
        </button>
      </div>

      {criando && (
        <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, margin: '12px 0' }}>
          <label>Criança
            <select value={nova.id_aluno} onChange={(e) => setNova({ ...nova, id_aluno: Number(e.target.value) })}>
              <option value={0}>Selecione...</option>
              {criancas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </label>
          <label>Especialidade
            <select value={nova.especialidade} onChange={(e) => setNova({ ...nova, especialidade: e.target.value })}>
              <option value="">Selecione...</option>
              {ESPECIALIDADES.map((e2) => <option key={e2.valor} value={e2.valor}>{e2.label}</option>)}
            </select>
          </label>
          {nova.especialidade === 'outra' && (
            <label>Qual especialidade?
              <input value={nova.especialidade_outra}
                onChange={(e) => setNova({ ...nova, especialidade_outra: e.target.value })} />
            </label>
          )}
          <label>Profissional
            <input value={nova.profissional} placeholder="Nome de quem atende"
              onChange={(e) => setNova({ ...nova, profissional: e.target.value })} />
          </label>
          <label>Registro no conselho (CRFa, CRP, CREFITO…)
            <input value={nova.registro_conselho}
              onChange={(e) => setNova({ ...nova, registro_conselho: e.target.value })} />
          </label>
          <label>Clínica
            <input value={nova.clinica} onChange={(e) => setNova({ ...nova, clinica: e.target.value })} />
          </label>
          <label>Telefone
            <input value={nova.telefone} onChange={(e) => setNova({ ...nova, telefone: e.target.value })} />
          </label>
          <label>E-mail
            <input value={nova.email} onChange={(e) => setNova({ ...nova, email: e.target.value })} />
          </label>
          <label>Frequência
            <input value={nova.frequencia} placeholder="Ex.: semanal, às terças"
              onChange={(e) => setNova({ ...nova, frequencia: e.target.value })} />
          </label>
          <label>Desde quando
            <input type="date" value={nova.data_inicio}
              onChange={(e) => setNova({ ...nova, data_inicio: e.target.value })} />
          </label>
          <label>Orientações do profissional para a creche
            <textarea rows={3} value={nova.orientacoes_para_creche}
              placeholder="O que a equipe deve fazer na rotina da sala — e o que deve evitar."
              onChange={(e) => setNova({ ...nova, orientacoes_para_creche: e.target.value })} />
          </label>
          <p style={{ fontSize: 13, color: '#666' }}>
            O contato só ficará visível depois que a autorização da família for registrada.
          </p>
          <button className="btn-primario" onClick={criarTerapia}>Cadastrar terapia</button>
        </div>
      )}

      <h3>Terapias</h3>
      {terapias.length === 0 && <p style={{ color: '#888' }}>Nenhuma terapia cadastrada.</p>}
      {terapias.map((t) => (
        <div key={t.id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, margin: '10px 0' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <strong>{t.aluno?.nome || `Criança ${t.id_aluno}`}</strong>
            <span>
              {t.especialidade === 'outra'
                ? (t.especialidade_outra || 'Outra')
                : rotulo(ESPECIALIDADES, t.especialidade)}
            </span>
            <span style={{ color: '#666' }}>
              {t.profissional}{t.registro_conselho ? ` (${t.registro_conselho})` : ''}
              {t.clinica ? ` · ${t.clinica}` : ''}
            </span>
            {t.frequencia && <span style={{ color: '#666', fontSize: 13 }}>{t.frequencia}</span>}
            <select value={t.situacao} onChange={(e) => mudarSituacao(t, e.target.value)}
              style={{ marginLeft: 'auto' }}>
              {SITUACOES.map((s) => <option key={s.valor} value={s.valor}>{s.label}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
            desde {formatarDataBR(t.data_inicio)}{t.data_fim ? ` até ${formatarDataBR(t.data_fim)}` : ''}
          </div>

          {t.orientacoes_para_creche && (
            <div style={{ background: '#f1f8e9', padding: 8, borderRadius: 6, marginTop: 8 }}>
              <strong style={{ fontSize: 13 }}>Orientação para a sala:</strong>{' '}
              <span style={{ whiteSpace: 'pre-wrap' }}>{t.orientacoes_para_creche}</span>
            </div>
          )}

          {/* O contato não chega do servidor sem autorização. */}
          <div style={{ marginTop: 8, fontSize: 14 }}>
            {t.contato.liberado ? (
              <>
                <span>📞 {t.contato.telefone || '—'}</span>
                {t.contato.email && <span> · ✉️ {t.contato.email}</span>}
                <span style={{ color: '#666', fontSize: 12 }}>
                  {' '}(autorizado por {t.autorizacao_por}
                  {t.autorizacao_em ? ` em ${formatarDataBR(t.autorizacao_em)}` : ''})
                </span>
                <button style={{ marginLeft: 8 }} onClick={() => revogarContato(t)}>revogar</button>
              </>
            ) : (
              <>
                <span style={{ color: '#a15c00' }}>🔒 {t.contato.motivo}</span>
                <button style={{ marginLeft: 8 }} onClick={() => autorizarContato(t)}>
                  Registrar autorização
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      {registrando && (
        <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, margin: '12px 0' }}>
          <label>Criança
            <select value={novoAcomp.id_aluno}
              onChange={(e) => setNovoAcomp({ ...novoAcomp, id_aluno: Number(e.target.value) })}>
              <option value={0}>Selecione...</option>
              {criancas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </label>
          <label>Tipo
            <select value={novoAcomp.tipo} onChange={(e) => setNovoAcomp({ ...novoAcomp, tipo: e.target.value })}>
              {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </label>
          <label>Terapia relacionada (opcional)
            <select value={novoAcomp.id_terapia}
              onChange={(e) => setNovoAcomp({ ...novoAcomp, id_terapia: e.target.value })}>
              <option value="">Nenhuma</option>
              {terapias
                .filter((t) => !novoAcomp.id_aluno || t.id_aluno === novoAcomp.id_aluno)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.profissional} — {rotulo(ESPECIALIDADES, t.especialidade)}
                  </option>
                ))}
            </select>
          </label>
          <label>Data
            <input type="date" value={novoAcomp.data}
              onChange={(e) => setNovoAcomp({ ...novoAcomp, data: e.target.value })} />
          </label>
          <label>Participantes
            <input value={novoAcomp.participantes} placeholder="Quem esteve: equipe, família, profissionais"
              onChange={(e) => setNovoAcomp({ ...novoAcomp, participantes: e.target.value })} />
          </label>
          <label>O que foi tratado
            <textarea rows={3} value={novoAcomp.resumo}
              onChange={(e) => setNovoAcomp({ ...novoAcomp, resumo: e.target.value })} />
          </label>
          <label>Encaminhamentos
            <textarea rows={2} value={novoAcomp.encaminhamentos}
              onChange={(e) => setNovoAcomp({ ...novoAcomp, encaminhamentos: e.target.value })} />
          </label>
          <label>Próxima ação em (opcional)
            <input type="date" value={novoAcomp.proxima_acao_em}
              onChange={(e) => setNovoAcomp({ ...novoAcomp, proxima_acao_em: e.target.value })} />
          </label>
          <button className="btn-primario" onClick={registrarAcomp}>Registrar</button>
        </div>
      )}

      <h3 style={{ marginTop: 20 }}>Histórico do acompanhamento</h3>
      {acomps.length === 0 && <p style={{ color: '#888' }}>Nenhum registro ainda.</p>}
      {acomps.map((a) => (
        <div key={a.id} style={{ borderLeft: '3px solid #ccc', padding: '6px 10px', margin: '8px 0' }}>
          <div style={{ fontSize: 13, color: '#666' }}>
            {formatarDataBR(a.data)} · {rotulo(TIPOS, a.tipo)} ·{' '}
            {a.aluno?.nome || `Criança ${a.id_aluno}`}
            {a.terapia?.profissional ? ` · ${a.terapia.profissional}` : ''}
            {a.autor?.nome ? ` · registrado por ${a.autor.nome}` : ''}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{a.resumo}</div>
          {a.participantes && (
            <div style={{ fontSize: 13, color: '#666' }}>Participantes: {a.participantes}</div>
          )}
          {a.encaminhamentos && (
            <div style={{ fontSize: 13 }}><strong>Encaminhamentos:</strong> {a.encaminhamentos}</div>
          )}
          {a.proxima_acao_em && (
            <div style={{ fontSize: 13, color: a.proxima_acao_em < hojeISO() ? '#c62828' : '#666' }}>
              Próxima ação: {formatarDataBR(a.proxima_acao_em)}
              {a.proxima_acao_em < hojeISO() ? ' — atrasada' : ''}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
